// src/App.js
// ──────────────────────────────────────────────────────────────────────────
// University routes + the new GlobalStyle. Kept your Lenis smooth scroll and
// Framer page transitions. Removed SocketProvider (no login/realtime needed
// for a public site) — add it back if you later build a portal.
// ──────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ReactLenis } from 'lenis/react';
import { AnimatePresence, motion } from 'framer-motion';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { gsap } from 'gsap';

import { GlobalStyle } from './theme/theme';
import { SiteContentProvider } from './context/SiteContentContext';
import { AuthProvider } from './context/AuthContext';

import HomeContent   from './pages/HomeContent';
import Programs       from './pages/Programs';
import ProgramDetail  from './pages/ProgramDetail';
import Admissions     from './pages/Admissions';
import About          from './pages/About';
import Library        from './pages/Library';
import Apply          from './pages/Apply';
import Scholarships    from './pages/Scholarships';
import Accommodation   from './pages/Accommodation';
import Fees            from './pages/Fees';
import NotFound        from './pages/NotFound';
import Login           from './pages/Login';
import Admin           from './pages/Admin';
import StudentPortal   from './pages/StudentPortal';
import StaffPortal     from './pages/StaffPortal';
import SetPassword     from './pages/SetPassword';

import Loader        from './components/Loader';
import ErrorBoundary from './components/ErrorBoundary';
import enableProtection from './utils/protect';
import ProtectedRoute from './components/ProtectedRoute';

// ── Pages still on the old aesthetic — re-skin next (see README) ──────────
import News    from './pages/News';      // News & announcements (works as-is)

import Contact from './pages/Contact';   // → repurpose for enquiries
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse    from './pages/TermsOfUse';

gsap.registerPlugin(ScrollTrigger);

const PageTransition = ({ children }) => (
  <motion.div
    initial={{ opacity: 1 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0, y: -16 }}
    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    onAnimationComplete={() => ScrollTrigger.refresh()}
  >
    {children}
  </motion.div>
);

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/"               element={<PageTransition><HomeContent /></PageTransition>} />
        <Route path="/programs"       element={<PageTransition><Programs /></PageTransition>} />
        <Route path="/programs/:id"   element={<PageTransition><ProgramDetail /></PageTransition>} />
        <Route path="/admissions"     element={<PageTransition><Admissions /></PageTransition>} />
        <Route path="/news"           element={<PageTransition><News /></PageTransition>} />
        <Route path="/about"          element={<PageTransition><About /></PageTransition>} />
        <Route path="/library"        element={<ProtectedRoute><PageTransition><Library /></PageTransition></ProtectedRoute>} />
        <Route path="/login"          element={<PageTransition><Login /></PageTransition>} />
        <Route path="/admin"          element={<PageTransition><Admin /></PageTransition>} />
        <Route path="/portal/student"  element={<ProtectedRoute><PageTransition><StudentPortal /></PageTransition></ProtectedRoute>} />
        <Route path="/portal/staff"    element={<ProtectedRoute><PageTransition><StaffPortal /></PageTransition></ProtectedRoute>} />
        <Route path="/set-password"   element={<ProtectedRoute><PageTransition><SetPassword /></PageTransition></ProtectedRoute>} />
        <Route path="/apply"          element={<PageTransition><Apply /></PageTransition>} />
        <Route path="/scholarships"   element={<PageTransition><Scholarships /></PageTransition>} />
        <Route path="/accommodation"  element={<PageTransition><Accommodation /></PageTransition>} />
        <Route path="/fees"           element={<PageTransition><Fees /></PageTransition>} />
        <Route path="/contact"        element={<PageTransition><Contact /></PageTransition>} />
        <Route path="/privacy"        element={<PrivacyPolicy />} />
        <Route path="/terms"          element={<TermsOfUse />} />
        <Route path="*"               element={<PageTransition><NotFound /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    enableProtection(); // light anti-copy deterrents (production only)

    const reveal = () => setReady(true);
    const minDelay = setTimeout(reveal, 700);   // show splash briefly even when fast
    const safety = setTimeout(reveal, 6000);     // never hang forever
    if (document.readyState === 'complete') {
      clearTimeout(minDelay);
      setTimeout(reveal, 400);
    } else {
      window.addEventListener('load', reveal);
    }
    return () => { clearTimeout(minDelay); clearTimeout(safety); window.removeEventListener('load', reveal); };
  }, []);

  return (
    <SiteContentProvider>
      <AuthProvider>
      <GlobalStyle />
      <Loader visible={!ready} />
      <ErrorBoundary>
        <ReactLenis root options={{ lerp: 0.1, duration: 1.5, smoothTouch: false }}>
          <Router>
            <AnimatedRoutes />
          </Router>
        </ReactLenis>
      </ErrorBoundary>
      </AuthProvider>
    </SiteContentProvider>
  );
}

export default App;
